import { useState, useCallback } from 'react';
import { App } from 'antd';
import { submitFeedback } from '../../../apis/MessageService';

export type FeedbackState = 'none' | 'positive' | 'negative';

export interface UseFeedbackResult {
  feedbackState: FeedbackState;
  modalOpen: boolean;
  comment: string;
  submitting: boolean;
  setComment: (v: string) => void;
  submitPositive: () => Promise<void>;
  openNegativeModal: () => void;
  submitNegative: () => Promise<void>;
  cancelNegativeModal: () => void;
}

export function useFeedback(tenantId: string, threadId: string, messageId: string): UseFeedbackResult {
  const { message } = App.useApp();
  const [feedbackState, setFeedbackState] = useState<FeedbackState>('none');
  const [modalOpen, setModalOpen] = useState(false);
  const [comment, setComment] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const submitPositive = useCallback(async () => {
    if (feedbackState !== 'none') return;
    try {
      await submitFeedback(tenantId, threadId, messageId, { rating: 'positive', comment: null, trace_id: null });
      setFeedbackState('positive');
      message.success('Thank you for your feedback!');
    } catch (err) {
      if ((err as any)?.response?.status === 409) {
        setFeedbackState('positive');
        message.info('Feedback already submitted for this message.');
      } else {
        message.error('Failed to submit feedback.');
      }
    }
  }, [feedbackState, tenantId, threadId, messageId]);

  const openNegativeModal = useCallback(() => {
    if (feedbackState !== 'none') return;
    setModalOpen(true);
  }, [feedbackState]);

  const submitNegative = useCallback(async () => {
    setSubmitting(true);
    try {
      await submitFeedback(tenantId, threadId, messageId, {
        rating: 'negative',
        comment: comment.trim() || null,
        trace_id: null,
      });
      setFeedbackState('negative');
      setModalOpen(false);
      setComment('');
      message.success('Thank you for your feedback!');
    } catch (err) {
      if ((err as any)?.response?.status === 409) {
        setFeedbackState('negative');
        setModalOpen(false);
        message.info('Feedback already submitted for this message.');
      } else {
        message.error('Failed to submit feedback.');
      }
    } finally {
      setSubmitting(false);
    }
  }, [tenantId, threadId, messageId, comment]);

  const cancelNegativeModal = useCallback(() => {
    setModalOpen(false);
    setComment('');
  }, []);

  return {
    feedbackState,
    modalOpen,
    comment,
    submitting,
    setComment,
    submitPositive,
    openNegativeModal,
    submitNegative,
    cancelNegativeModal,
  };
}
